import { WorkHoursConfig, TaskBlock, CalendarEvent } from '../types/index';
import {
  startOfDay,
  endOfDay,
  addDays,
  parseISO,
  differenceInMinutes,
  setHours,
  setMinutes,
  isSameDay,
  format,
  isWithinInterval,
  max as dateMax,
  min as dateMin,
} from 'date-fns';

export interface DayCapacity {
  date: Date;
  dateStr: string;
  dayLabel: string;
  totalAvailableMinutes: number;
  occupiedMinutes: number;
  freeMinutes: number;
  occupancyPercent: number;
  isWorkDay: boolean;
  blocks: {
    taskId: string;
    taskName: string;
    taskType: string;
    projectName?: string;
    startTime: string;
    endTime: string;
    durationMinutes: number;
  }[];
  externalEvents: {
    title: string;
    startTime: string;
    endTime: string;
    durationMinutes: number;
  }[];
}

export interface WeekCapacity {
  days: DayCapacity[];
  totalAvailableMinutes: number;
  totalOccupiedMinutes: number;
  totalFreeMinutes: number;
  overallOccupancyPercent: number;
}

export interface FeasibilityResult {
  feasible: boolean;
  status: 'viable' | 'tight' | 'not_viable';
  availableMinutes: number;
  requiredMinutes: number;
  surplusMinutes: number;
  suggestedEndDate?: string;
  message: string;
}

const DAY_NAME_TO_NUMBER: Record<string, number> = {
  sunday: 0,
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
};

function parseTime(timeStr: string, date: Date): Date {
  const [hours, minutes] = timeStr.split(':').map(Number);
  return setMinutes(setHours(new Date(date.getTime()), hours), minutes);
}

function isWorkDay(date: Date, workDays: string[]): boolean {
  const dow = date.getDay();
  return workDays.some(name => DAY_NAME_TO_NUMBER[name.toLowerCase()] === dow);
}

function getWorkMinutesForDay(date: Date, config: WorkHoursConfig): number {
  if (!isWorkDay(date, config.work_days)) return 0;

  const morningStart = parseTime(config.morning_start, date);
  const morningEnd = parseTime(config.morning_end, date);
  const afternoonStart = parseTime(config.afternoon_start, date);
  const afternoonEnd = parseTime(config.afternoon_end, date);

  const morningMinutes = Math.max(0, differenceInMinutes(morningEnd, morningStart));
  const afternoonMinutes = Math.max(0, differenceInMinutes(afternoonEnd, afternoonStart));

  return morningMinutes + afternoonMinutes;
}

function getOccupiedMinutesForDay(
  date: Date,
  blocks: TaskBlock[],
  externalEvents: CalendarEvent[],
  config: WorkHoursConfig
): { taskMinutes: number; externalMinutes: number } {
  const dayStart = startOfDay(date);
  const dayEnd = endOfDay(date);

  const morningStart = parseTime(config.morning_start, date);
  const morningEnd = parseTime(config.morning_end, date);
  const afternoonStart = parseTime(config.afternoon_start, date);
  const afternoonEnd = parseTime(config.afternoon_end, date);

  const workWindows = [
    { start: morningStart, end: morningEnd },
    { start: afternoonStart, end: afternoonEnd },
  ];

  let taskMinutes = 0;
  for (const block of blocks) {
    const blockStart = parseISO(block.start_time);
    const blockEnd = parseISO(block.end_time);

    if (blockEnd <= dayStart || blockStart >= dayEnd) continue;

    for (const window of workWindows) {
      const overlapStart = dateMax([blockStart, window.start]);
      const overlapEnd = dateMin([blockEnd, window.end]);
      if (overlapStart < overlapEnd) {
        taskMinutes += differenceInMinutes(overlapEnd, overlapStart);
      }
    }
  }

  let externalMinutes = 0;
  for (const event of externalEvents) {
    const eventStart = parseISO(event.start_time);
    const eventEnd = parseISO(event.end_time);

    if (eventEnd <= dayStart || eventStart >= dayEnd) continue;

    for (const window of workWindows) {
      const overlapStart = dateMax([eventStart, window.start]);
      const overlapEnd = dateMin([eventEnd, window.end]);
      if (overlapStart < overlapEnd) {
        externalMinutes += differenceInMinutes(overlapEnd, overlapStart);
      }
    }
  }

  return { taskMinutes, externalMinutes };
}

export function calculateWeekCapacity(
  weekStartDate: Date,
  config: WorkHoursConfig,
  taskBlocks: TaskBlock[],
  externalEvents: CalendarEvent[]
): WeekCapacity {
  const days: DayCapacity[] = [];

  for (let i = 0; i < 7; i++) {
    const date = addDays(weekStartDate, i);
    const isWork = isWorkDay(date, config.work_days);
    const totalAvailableMinutes = getWorkMinutesForDay(date, config);
    const { taskMinutes, externalMinutes } = getOccupiedMinutesForDay(
      date, taskBlocks, externalEvents, config
    );
    const occupiedMinutes = taskMinutes + externalMinutes;
    const freeMinutes = Math.max(0, totalAvailableMinutes - occupiedMinutes);
    const occupancyPercent = totalAvailableMinutes > 0
      ? Math.min(100, Math.round((occupiedMinutes / totalAvailableMinutes) * 100))
      : 0;

    const dayStart = startOfDay(date);
    const dayEnd = endOfDay(date);

    const dayBlocks = taskBlocks
      .filter(b => {
        const bStart = parseISO(b.start_time);
        return bStart >= dayStart && bStart < dayEnd;
      })
      .map(b => ({
        taskId: b.task_id,
        taskName: (b.task as any)?.nombre || 'Tarea',
        taskType: (b.task as any)?.tipo || 'edicion',
        projectName: (b.task as any)?.project?.nombre,
        startTime: b.start_time,
        endTime: b.end_time,
        durationMinutes: b.duration_minutes,
      }));

    const dayExternalEvents = externalEvents
      .filter(e => {
        const eStart = parseISO(e.start_time);
        return eStart >= dayStart && eStart < dayEnd;
      })
      .map(e => ({
        title: e.title,
        startTime: e.start_time,
        endTime: e.end_time,
        durationMinutes: differenceInMinutes(parseISO(e.end_time), parseISO(e.start_time)),
      }));

    days.push({
      date,
      dateStr: format(date, 'yyyy-MM-dd'),
      dayLabel: format(date, 'EEE d'),
      totalAvailableMinutes,
      occupiedMinutes,
      freeMinutes,
      occupancyPercent,
      isWorkDay: isWork,
      blocks: dayBlocks,
      externalEvents: dayExternalEvents,
    });
  }

  const totalAvailableMinutes = days.reduce((sum, d) => sum + d.totalAvailableMinutes, 0);
  const totalOccupiedMinutes = days.reduce((sum, d) => sum + d.occupiedMinutes, 0);
  const totalFreeMinutes = days.reduce((sum, d) => sum + d.freeMinutes, 0);
  const overallOccupancyPercent = totalAvailableMinutes > 0
    ? Math.round((totalOccupiedMinutes / totalAvailableMinutes) * 100)
    : 0;

  return {
    days,
    totalAvailableMinutes,
    totalOccupiedMinutes,
    totalFreeMinutes,
    overallOccupancyPercent,
  };
}

export function checkTaskFeasibility(
  fechaInicio: string,
  fechaFin: string,
  horasEstimadas: number,
  config: WorkHoursConfig,
  taskBlocks: TaskBlock[],
  externalEvents: CalendarEvent[],
  excludeTaskId?: string
): FeasibilityResult {
  const requiredMinutes = horasEstimadas * 60;
  const startDate = parseISO(fechaInicio);
  const endDate = parseISO(fechaFin);
  const today = startOfDay(new Date());
  const effectiveStart = dateMax([startDate, today]);

  let availableMinutes = 0;
  let currentDate = startOfDay(effectiveStart);
  const lastDate = endOfDay(endDate);

  const filteredBlocks = excludeTaskId
    ? taskBlocks.filter(b => b.task_id !== excludeTaskId)
    : taskBlocks;

  while (currentDate <= lastDate) {
    const totalDay = getWorkMinutesForDay(currentDate, config);
    const { taskMinutes, externalMinutes } = getOccupiedMinutesForDay(
      currentDate, filteredBlocks, externalEvents, config
    );
    availableMinutes += Math.max(0, totalDay - taskMinutes - externalMinutes);
    currentDate = addDays(currentDate, 1);
  }

  const surplusMinutes = availableMinutes - requiredMinutes;

  if (surplusMinutes >= requiredMinutes * 0.2) {
    return {
      feasible: true,
      status: 'viable',
      availableMinutes,
      requiredMinutes,
      surplusMinutes,
      message: `Viable: ${formatMinutes(availableMinutes)} disponibles para ${formatMinutes(requiredMinutes)} necesarias`,
    };
  }

  if (surplusMinutes >= 0) {
    return {
      feasible: true,
      status: 'tight',
      availableMinutes,
      requiredMinutes,
      surplusMinutes,
      message: `Ajustado: solo ${formatMinutes(availableMinutes)} disponibles para ${formatMinutes(requiredMinutes)} necesarias`,
    };
  }

  const suggestedEnd = findSuggestedEndDate(
    startDate, requiredMinutes, config, filteredBlocks, externalEvents
  );

  return {
    feasible: false,
    status: 'not_viable',
    availableMinutes,
    requiredMinutes,
    surplusMinutes,
    suggestedEndDate: suggestedEnd ? format(suggestedEnd, 'yyyy-MM-dd') : undefined,
    message: `No viable: faltan ${formatMinutes(Math.abs(surplusMinutes))}. Necesitas ampliar el plazo.`,
  };
}

function findSuggestedEndDate(
  startDate: Date,
  requiredMinutes: number,
  config: WorkHoursConfig,
  taskBlocks: TaskBlock[],
  externalEvents: CalendarEvent[]
): Date | null {
  let accumulated = 0;
  const today = startOfDay(new Date());
  let currentDate = dateMax([startOfDay(startDate), today]);
  const maxSearch = 180;

  for (let i = 0; i < maxSearch; i++) {
    const totalDay = getWorkMinutesForDay(currentDate, config);
    const { taskMinutes, externalMinutes } = getOccupiedMinutesForDay(
      currentDate, taskBlocks, externalEvents, config
    );
    accumulated += Math.max(0, totalDay - taskMinutes - externalMinutes);

    if (accumulated >= requiredMinutes) {
      return currentDate;
    }
    currentDate = addDays(currentDate, 1);
  }

  return null;
}

function formatMinutes(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours === 0) return `${mins}min`;
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}min`;
}